import {
  animate,
  AnimatePresence,
  type AnimationPlaybackControls,
  type HTMLMotionProps,
  motion,
  MotionConfig,
  stagger,
  type Transition,
  usePresence,
  useReducedMotion,
} from 'motion/react';
import {
  type Key,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { cn } from './cn.ts';

/**
 * Пресеты движения платформы: быстро (120–220 мс), без «резиновости» в плотных списках — в духе Linear.
 * CSS-анимации слоёв Radix (styles/index.css) и кривые в tokens.css повторяют эти же значения.
 */
export const DURATION = { fast: 0.12, base: 0.16, slow: 0.22 } as const;

/** Кривые: `out` — быстрый старт и мягкая остановка (появление), `in` — разгон (уход), `inOut` — перестройка. */
export const EASE = {
  out: [0.2, 0.9, 0.3, 1],
  in: [0.4, 0, 1, 1],
  inOut: [0.4, 0, 0.2, 1],
} as const;

export const TRANSITION = {
  /** Появление. */
  enter: { duration: DURATION.base, ease: EASE.out },
  /** Уход — короче появления: закрытое не должно задерживаться. */
  exit: { duration: DURATION.fast, ease: EASE.in },
  /** Раскрытие и схлопывание по высоте. */
  expand: { duration: DURATION.slow, ease: EASE.out },
  /** Перемещение и смена размера (layout, layoutId): пружина без отскока. */
  layout: { type: 'spring', visualDuration: 0.22, bounce: 0 },
  /** Выезд панели или листа. */
  panel: { type: 'spring', visualDuration: 0.26, bounce: 0 },
  /**
   * Ширина колонки и высота блока: без пружины. У пружины длинный «хвост» — соседи перестраиваются
   * ещё десятки миллисекунд после того, как движение уже не видно, и текст в них дрожит.
   */
  resize: { duration: DURATION.slow, ease: EASE.inOut },
  /** Возврат после свайпа — единственное место с лёгким отскоком. */
  snapBack: { type: 'spring', visualDuration: 0.3, bounce: 0.2 },
} as const satisfies Record<string, Transition>;

/** Проявление: пустые состояния, контент после скелетона, смена содержимого. */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: TRANSITION.enter },
  exit: { opacity: 0, transition: TRANSITION.exit },
} as const;

/** Всплывающий слой (меню, окно, плашка): лёгкое увеличение с проявлением. */
export const popIn = {
  initial: { opacity: 0, scale: 0.97, y: 4 },
  animate: { opacity: 1, scale: 1, y: 0, transition: TRANSITION.enter },
  exit: { opacity: 0, scale: 0.98, y: 2, transition: TRANSITION.exit },
} as const;

/**
 * Раскрытие по высоте: секции, дополнительные поля, строки, которые появляются и схлопываются.
 * Высота анимируется до `auto`, поэтому у блока `overflow: hidden` на время движения.
 */
export const collapse = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: { height: TRANSITION.expand, opacity: { duration: DURATION.base, ease: EASE.out } },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: { height: { duration: DURATION.base, ease: EASE.inOut }, opacity: TRANSITION.exit },
  },
  style: { overflow: 'hidden' },
} as const;

type Side = 'top' | 'right' | 'bottom' | 'left';

const OFFSET: Readonly<Record<Side, { x?: number; y?: number }>> = {
  top: { y: -1 },
  right: { x: 1 },
  bottom: { y: 1 },
  left: { x: -1 },
};

/** Выезд панели с края: карточка справа, лист снизу на телефоне. `distance` — в пикселях. */
export function slideIn(from: Side, distance = 24) {
  const { x = 0, y = 0 } = OFFSET[from];
  return {
    initial: { opacity: 0, x: x * distance, y: y * distance },
    animate: { opacity: 1, x: 0, y: 0, transition: TRANSITION.panel },
    exit: { opacity: 0, x: x * distance, y: y * distance, transition: TRANSITION.exit },
  } as const;
}

/**
 * Строка списка: появляется раскрытием, при удалении схлопывается, соседи съезжают плавно.
 * Для коротких списков и списков без виртуализации: `layout` на каждой строке виртуального списка дорог.
 */
export const listItem = {
  layout: 'position',
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto', transition: TRANSITION.expand },
  exit: { opacity: 0, height: 0, transition: { duration: DURATION.base, ease: EASE.inOut } },
  transition: { layout: TRANSITION.layout },
  style: { overflow: 'hidden' },
} as const;

/**
 * Каскад для коротких списков (до десятка элементов) — один раз при появлении, шаг 25 мс:
 * `{...cascade.parent}` на контейнер, `{...cascade.child}` на элементы.
 */
export const cascade = {
  parent: {
    initial: 'hidden',
    animate: 'shown',
    variants: { hidden: {}, shown: { transition: { delayChildren: stagger(0.025) } } },
  },
  child: {
    variants: {
      hidden: { opacity: 0, y: 4 },
      shown: { opacity: 1, y: 0, transition: TRANSITION.enter },
    },
  },
} as const;

/** Изменения высоты чаще этого — содержимое двигается само (вложенная анимация), блок лишь следует за ним. */
const FOLLOW_WINDOW_MS = 48;

/**
 * Блок, высота которого плавно следует за содержимым: скелетон сменился строками, пустое состояние —
 * списком, появилась плашка. Без него всё, что ниже блока, перепрыгивает на новое место за один кадр.
 *
 * Высоту меряет `ResizeObserver` по внутренней обёртке, а плавно меняет внешняя; сам блок остаётся с
 * `height: auto` — движение идёт, только пока высота меняется, и `overflow: hidden` на это время.
 * При «Уменьшить движение» (и в среде без `ResizeObserver`) блок ничего не делает.
 */
export function SmoothHeight({ children, className }: { children: ReactNode; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const outer = box.current;
    const inner = content.current;
    if (!outer || !inner || reduce || typeof ResizeObserver === 'undefined') return undefined;
    /** Высота, которую блок показывает сейчас (или к которой идёт); пока не знаем — null. */
    let shown: number | null = null;
    let running: AnimationPlaybackControls | undefined;
    let lastChange = 0;
    const settle = () => {
      outer.style.height = '';
      outer.style.overflow = '';
      running = undefined;
    };
    const observer = new ResizeObserver((entries) => {
      // Дробная высота без влияния transform у предков (окно, которое само увеличивается при открытии).
      const next = entries[0]?.borderBoxSize[0]?.blockSize ?? inner.offsetHeight;
      // Первое уведомление — исходная высота, а не изменение; доли пикселя — не изменение.
      if (shown === null) {
        shown = next;
        return;
      }
      if (Math.abs(next - shown) < 0.5) return;
      // Содержимое меняется кадр за кадром само (внутри раскрывается свой блок): догонять его вторым
      // движением незачем — оно запаздывало бы и обрезало растущее. Следуем без своей анимации.
      const now = performance.now();
      const selfMoving = now - lastChange < FOLLOW_WINDOW_MS;
      lastChange = now;
      if (selfMoving) {
        running?.stop();
        settle();
        shown = next;
        return;
      }
      // Прерванное движение продолжается с той высоты, что видна на экране.
      const from = running ? outer.getBoundingClientRect().height : shown;
      running?.stop();
      shown = next;
      // До первой отрисовки блок уже держит прежнюю высоту: иначе один кадр показал бы новую.
      outer.style.height = `${String(from)}px`;
      outer.style.overflow = 'hidden';
      running = animate(outer, { height: [from, next] }, { ...TRANSITION.resize, onComplete: settle });
    });
    observer.observe(inner);
    return () => {
      observer.disconnect();
      running?.stop();
      settle();
    };
  }, [reduce]);

  return (
    <div ref={box} className={className}>
      <div ref={content} className="relative flow-root">
        {children}
      </div>
    </div>
  );
}

/**
 * Смена содержимого по ключу без скачка: старое гаснет, новое проявляется, высота блока плавно идёт к новой.
 * Для вкладок и переключателей («Доска / Таблица»), состояний «загрузка → список → пусто».
 * Уходящее содержимое выходит из потока (`popLayout`) — новое сразу занимает место, не дожидаясь исхода.
 */
export function ContentSwap({
  swapKey,
  children,
  className,
  contentClassName,
}: {
  swapKey: Key;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <SmoothHeight {...(className === undefined ? {} : { className })}>
      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={swapKey}
          {...fadeIn}
          {...(contentClassName === undefined ? {} : { className: contentClassName })}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </SmoothHeight>
  );
}

/**
 * Содержимое проявляется, когда меняется `watch` (раздел, адрес страницы): страница под вкладками
 * или в колонке настроек не «выскакивает» на месте предыдущей. Без перемонтирования и без задержки —
 * анимация идёт по готовому содержимому до первой отрисовки, как у смены раздела в оболочке.
 * Первый показ не анимируется: его проявляет тот, кто показал сам блок.
 */
export function FadeOnChange({
  watch,
  children,
  className,
}: {
  watch: string;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const shown = useRef<string | null>(null);
  useLayoutEffect(() => {
    const first = shown.current === null;
    const changed = shown.current !== watch;
    shown.current = watch;
    if (first || !changed || !ref.current) return undefined;
    const appear = animate(ref.current, { opacity: [0, 1] }, TRANSITION.enter);
    return () => {
      appear.complete();
    };
  }, [watch]);
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

/** Снять размеры и сдвиг, которые движение ставило элементу: дальше им управляют классы. */
function clearBox(el: HTMLElement) {
  el.style.removeProperty('width');
  el.style.removeProperty('height');
  el.style.removeProperty('translate');
}

/**
 * Окно меняет размер и место сменой классов (свернуть, развернуть на весь экран) — это прыжок. Хук ловит
 * смену `state`, сравнивает прежний прямоугольник с новым и «доезжает» от одного к другому настоящими
 * шириной и высотой (содержимое не растягивается, как при масштабировании) и сдвигом `translate`.
 * Классы остаются источником истины: по окончании ширина, высота и сдвиг снимаются. Окну нужен
 * `overflow: hidden` — пока оно идёт, содержимое просто обрезается.
 *
 * Откуда ехать: если окно меняют его же кнопки, вызывается `capture()` перед сменой — тогда известен и
 * прежний размер, и прежнее место; если состояние сменил кто-то снаружи — берётся прежний размер, а
 * место считается тем, где окно встанет само (окно в ряду). Прерванное движение продолжается с того
 * размера, что виден на экране. При «Уменьшить движение» окно просто меняется.
 *
 * Возвращает `capture`.
 */
export function useBoxMorph(ref: RefObject<HTMLElement | null>, state: string, instant = false): () => void {
  const reduce = useReducedMotion();
  /** Размер после последней перерисовки без движения; пока идёт движение, не обновляется. */
  const size = useRef<{ width: number; height: number } | null>(null);
  /** Прямоугольник окна в момент, когда его меняют кнопки окна. */
  const before = useRef<DOMRect | null>(null);
  const running = useRef<AnimationPlaybackControls | null>(null);
  const shown = useRef(state);
  const latest = useRef({ reduce, instant });
  useLayoutEffect(() => {
    latest.current = { reduce, instant };
  });

  const capture = useCallback(() => {
    before.current = ref.current?.getBoundingClientRect() ?? null;
  }, [ref]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const live = el.getBoundingClientRect();
    const origin = before.current;
    before.current = null;
    if (shown.current === state) {
      if (!running.current) size.current = { width: live.width, height: live.height };
      return;
    }
    shown.current = state;
    // «Прыжок» без движения (полный экран): размеры не анимируем, только снимаем прежние. Иначе
    // окно «доезжало» бы от старого размера, а незавершённое движение оставляло его маленьким —
    // поверх `inset-0` остались бы инлайновые ширина и высота, и вокруг окна был бы виден интерфейс.
    if (latest.current.instant) {
      running.current?.stop();
      running.current = null;
      clearBox(el);
      const jump = el.getBoundingClientRect();
      size.current = { width: jump.width, height: jump.height };
      return;
    }
    // Если окно шло, оно стоит там, докуда дошло: оттуда и поедет дальше.
    const from = running.current ? { width: live.width, height: live.height } : size.current;
    running.current?.stop();
    running.current = null;
    clearBox(el);
    const to = el.getBoundingClientRect();
    size.current = { width: to.width, height: to.height };
    const width = origin?.width ?? from?.width;
    const height = origin?.height ?? from?.height;
    if (width === undefined || height === undefined || latest.current.reduce) return;
    // Встаём в прежний размер и меряем, где окно оказалось само, — разница и есть сдвиг.
    el.style.width = `${String(width)}px`;
    el.style.height = `${String(height)}px`;
    const start = el.getBoundingClientRect();
    const dx = origin ? origin.left - start.left : 0;
    const dy = origin ? origin.top - start.top : 0;
    const still =
      Math.abs(width - to.width) < 0.5 &&
      Math.abs(height - to.height) < 0.5 &&
      Math.abs(dx) < 0.5 &&
      Math.abs(dy) < 0.5;
    if (still) {
      clearBox(el);
      return;
    }
    const finish = () => {
      clearBox(el);
      running.current = null;
      const rest = el.getBoundingClientRect();
      size.current = { width: rest.width, height: rest.height };
    };
    running.current = animate(
      el,
      {
        width: [width, to.width],
        height: [height, to.height],
        translate: [`${String(dx)}px ${String(dy)}px`, '0px 0px'],
      },
      { ...TRANSITION.resize, onComplete: finish },
    );
  });

  useEffect(() => {
    const el = ref.current;
    // Размер окна браузера меняет и размер окна на странице, пока оно стоит.
    const onResize = () => {
      if (running.current || !el) return;
      const rect = el.getBoundingClientRect();
      size.current = { width: rect.width, height: rect.height };
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      running.current?.stop();
      running.current = null;
      if (el) clearBox(el);
    };
  }, [ref]);

  return capture;
}

/**
 * Окно в ряду из нескольких (flex-полоса, `gap` в пикселях): появляясь, оно не выталкивает соседей
 * за кадр, а уходя — не оставляет за собой дыру. Окно стоит на месте, а его место в ряду
 * раскрывается и схлопывается отрицательным отступом слева — соседи плавно сдвигаются. Пока окно одно
 * (или вне ряду, на весь экран), `active` ложно и ничего не происходит.
 *
 * Уход дожидается движения через `usePresence` (родитель — `AnimatePresence`). Возвращает `present`:
 * `false`, пока окно уходит, — такое окно не должно принимать нажатия.
 */
export function useStripSlot(ref: RefObject<HTMLElement | null>, active: boolean, gap: number): boolean {
  const reduce = useReducedMotion();
  const [present, safeToRemove] = usePresence();
  const latest = useRef({ active, gap, reduce, safeToRemove });
  useLayoutEffect(() => {
    latest.current = { active, gap, reduce, safeToRemove };
  });
  // Появление — только при монтировании: смена `active` у стоящего окна ничего не двигает.
  const mounted = useRef({ active, gap, reduce });
  useLayoutEffect(() => {
    const el = ref.current;
    const first = mounted.current;
    if (!el || !first.active || first.reduce === true) return undefined;
    const slot = el.getBoundingClientRect().width + first.gap;
    el.style.marginLeft = `${String(-slot)}px`;
    const slide = animate(
      el,
      { marginLeft: [-slot, 0] },
      {
        ...TRANSITION.resize,
        onComplete: () => {
          el.style.removeProperty('margin-left');
        },
      },
    );
    return () => {
      slide.stop();
      el.style.removeProperty('margin-left');
    };
  }, [ref]);
  useEffect(() => {
    if (present) return;
    const el = ref.current;
    const { active: slotted, gap: space, reduce: calm, safeToRemove: done } = latest.current;
    if (!el || !slotted || calm === true) {
      done?.();
      return;
    }
    const slot = el.getBoundingClientRect().width + space;
    void animate(el, { marginLeft: -slot }, { ...TRANSITION.resize, onComplete: () => done?.() });
  }, [present, ref]);
  return present;
}

/** Ширина колонки, которая растёт из нуля: соседняя колонка сужается в такт, без скачка. */
function columnPush(width: number, instant: boolean) {
  const none = { duration: 0 };
  return {
    initial: { width: 0, opacity: 0 },
    animate: {
      width,
      opacity: 1,
      transition: instant
        ? none
        : { width: TRANSITION.resize, opacity: { duration: DURATION.base, ease: EASE.out } },
    },
    exit: {
      width: 0,
      opacity: 0,
      transition: instant
        ? none
        : { width: TRANSITION.resize, opacity: { duration: DURATION.fast, ease: EASE.in } },
    },
  } as const;
}

export interface SidePaneProps extends Omit<
  HTMLMotionProps<'aside'>,
  'initial' | 'animate' | 'exit' | 'transition' | 'children'
> {
  open: boolean;
  /** Ширина колонки рядом с содержимым, px. */
  width: number;
  /**
   * `true` — колонка стоит рядом и сужает соседа; `false` — лист поверх содержимого (телефон):
   * выезжает с края, ширину не трогает. Классы листа задаёт `className`.
   */
  push?: boolean;
  /** Классы внутренней колонки: фон, рамка. Её ширина фиксирована, поэтому текст в ней не перетекает. */
  innerClassName?: string;
  children: ReactNode;
}

/**
 * Боковая панель рядом с основным содержимым (тред, сведения, закрепы). Открытие и закрытие меняют
 * ширину колонки плавно; внутри панели — обёртка фиксированной ширины, поэтому при выезде её содержимое
 * не перестраивается построчно, а открывается, как из-за шторки. Рамка — у обёртки, не у самой колонки:
 * у закрытой колонки нет ни единого пикселя.
 *
 * Если ширина меняется у уже открытой панели (тред шире сведений), колонка тоже растёт плавно.
 */
export function SidePane({
  open,
  width,
  push = true,
  className,
  innerClassName,
  children,
  ...rest
}: SidePaneProps) {
  const reduce = useReducedMotion() === true;
  const motionProps = push ? columnPush(width, reduce) : slideIn('right');
  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.aside
          key="pane"
          {...rest}
          {...motionProps}
          className={cn(push && 'shrink-0 overflow-hidden', className)}
        >
          <div
            className={cn('flex min-h-0 flex-1 flex-col', innerClassName)}
            {...(push ? { style: { width } } : {})}
          >
            {children}
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Корень движения: системная настройка «Уменьшить движение» отключает сдвиги и layout-анимации
 * (прозрачность остаётся), по умолчанию — переход появления. Ставится один раз в корне приложения.
 */
export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <MotionConfig reducedMotion="user" transition={TRANSITION.enter}>
      {children}
    </MotionConfig>
  );
}
